// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { DataState, MainPanel, TimePeriod } from "types";
import { ActionType, IAction, IActionSortTable, IActionTypeEdit, raiseAction } from "actions";
import { IState } from "states/states";
import { StopReason } from "model/connector";
import { IOcppCharger, IOcppChargingSession, IRfid, ITariff } from "states/ocppstates";
import { IChargingSessionFilter, ITableSort } from "states/guistates";
import { formatTimestamp, isSet, roundFix } from "utils";
import { isStateDirty, makeSortHeader, parseHtmlInputValue, tabClickHandler } from "guiutils";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";
import PeriodSelect from "view/comp/PeriodSelect";
import TrendHeading from "view/comp/TrendHeading";
import SearchFilter from "view/comp/SearchFilter";

// sort key of table
const TABLE_NAME = "t_chargingsessions";

interface IChargingSessionListExtProps {
  isElementSelected?: boolean;
}

interface IChargingSessionListProps {
  lastUpdate: number;
  isRfidUser: boolean;
  chargers: IOcppCharger[];
  chargingSession: IOcppChargingSession[];
  chargingSessionFilter: IChargingSessionFilter;
  knownRfids: string[];
  rfids: IRfid[];
  tariffs: ITariff[];
  dataState: DataState;
  tableSort: ITableSort | undefined;
  chargerId?: number | undefined;
  ocppChargers: IOcppCharger[];
}

interface IChargingSessionListState {
  filterText: string;
}

const mapStateToProps = (state: IState) => {
  const props: IChargingSessionListProps = {
    lastUpdate: state.scheduler.pendingOcppSessionState.lastData ?? 0,
    isRfidUser: !!state.site.user?.isRfidUser,
    chargers: state.ocpp.charger,
    chargingSession: state.ocpp.chargingSession,
    chargingSessionFilter: state.gui.chargingSessionFilter,
    knownRfids: state.gui.rfidList,
    rfids: state.ocpp.rfid,
    tariffs: state.ocpp.siteTariff,
    dataState: state.scheduler.ocppSessionState,
    tableSort: state.gui.tableSort[TABLE_NAME] ?? {
      column: "startTimestamp",
      isSortAscending: true,
    },
    chargerId: undefined,
    ocppChargers: state.ocpp.charger,
  };

  return props;
};

interface IChargingSessionListDispatch {
  onItemChanged: (event: React.FormEvent<HTMLInputElement>) => void;
  onValueChange: (name: string, value: string | number | undefined) => void;
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IChargingSessionListExtProps,
) => {
  const dispatchProps: IChargingSessionListDispatch = {
    onItemChanged: (event: React.FormEvent<HTMLInputElement>) => {
      const editPayload: IActionTypeEdit = {
        name: event.currentTarget.name,
        value: parseHtmlInputValue(event.currentTarget),
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_VALUE, editPayload));
      dispatch(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_REQ));
    },
    onValueChange: (name: string, value: string | number | undefined) => {
      const editPayload: IActionTypeEdit = {
        name,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_VALUE, editPayload));
    },
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  periodTitle: {
    id: "Charging Sessions",
  },
  stopReasonDisconnected: {
    id: "disconnected",
  },
  stopReasonRefused: {
    id: "refused",
  },
  stopReasonExpired: {
    id: "expired",
  },
  stopReasonOther: {
    id: "other",
  },
  msg_user_caption: {
    id: "Select Charger",
  },
  placeholderSearchField: {
    id: "Search for chargers...",
  },
  toolTipSearchField: {
    id: "Search for charger name, vendor or serial number. To search for multiple fields, separate search texts by a space!",
  },
});

class ChargingSessionListComponent extends React.Component<
  IChargingSessionListExtProps &
    IChargingSessionListProps &
    IChargingSessionListDispatch &
    WrappedComponentProps &
    RoutedProps
> {
  public state: IChargingSessionListState = {
    filterText: this.props.chargingSessionFilter.chargerName ?? "",
  };

  public render() {
    const filterTextsLowerCase = this.state.filterText.toLocaleLowerCase().split(" ");
    const showUser =
      !this.props.isRfidUser && this.props.chargingSession.some((curItem) => isSet(curItem.rfid));
    const showCar =
      !this.props.isRfidUser &&
      this.props.chargingSession.some((curSession) =>
        isSet(this.props.rfids.find((curRfid) => curSession.rfidId === curRfid.id)?.carName),
      );
    const showCosts = this.props.chargingSession.some((curItem) => isSet(curItem.fk_tariff));
    const showStopReason = this.props.chargingSession.some((curItem) => isSet(curItem.stopReason));

    const dataItems = this.getSortedSessions()
      .filter((curItem) => {
        const charger = this.props.chargers.find(
          (searchItem) => searchItem.fk_charger === curItem.fk_charger,
        );
        return (
          isSet(charger) &&
          filterTextsLowerCase.every(
            (curFilter) =>
              curFilter === "" ||
              charger.name?.toLocaleLowerCase().includes(curFilter) ||
              charger.chargepoint.toLocaleLowerCase().includes(curFilter) ||
              charger.serial?.toLocaleLowerCase().includes(curFilter) ||
              charger.vendor?.toLocaleLowerCase().includes(curFilter),
          )
        );
      })
      .map((curItem) => {
        return this.renderSession(curItem, showUser, showCosts, showStopReason, showCar);
      });

    return (
      <div id="chargingsessionlist">
        {!this.props.isRfidUser && this.renderFilter()}
        <div className="headerbuttons mb-2">
          <div className="left">
            <TrendHeading
              identifier="chargingSessionFilter"
              includeDate={!isStateDirty(this.props.dataState)}
              title={this.props.intl.formatMessage(messages.periodTitle)}
            />
            <div className="searchfeedback">{this.renderSearchFeedback()}</div>
            <div className="d-flex searchfilter">
              {this.props.isRfidUser && this.renderChargerFilter()}
            </div>
          </div>

          {this.props.isRfidUser && (
            <PeriodSelect
              identifier="chargingSessionFilter"
              updateAction={ActionType.ACT_GET_OCPP_SESSIONS_REQ}
              periodOptions={[TimePeriod.MONTH, TimePeriod.WEEK, TimePeriod.DAY, TimePeriod.CUSTOM]}
              className="autoMarginLeft align-content-end"
              style={{ float: "right", marginTop: 5 }}
            />
          )}
        </div>

        <div id="sessiontable">
          {this.props.chargingSession.length === 0 ? (
            <div className="emptytable pb-2">
              <FormattedMessage id="No charging sessions were found." />
            </div>
          ) : (
            <table id={TABLE_NAME} className="datatable fullwidthwhite">
              <thead>
                <tr>
                  {this.makeSortHeader("startTimestamp", <FormattedMessage id="Start" />)}
                  {this.makeSortHeader("stopTimestamp", <FormattedMessage id="End" />, [
                    "optional keep-print",
                  ])}
                  {this.makeSortHeader("fk_charger", <FormattedMessage id="Charger" />, [
                    "optional",
                    "charger-col",
                  ])}
                  {showUser && this.makeSortHeader("rfid", <FormattedMessage id="User" />)}
                  {showCar &&
                    this.makeSortHeader("carName", <FormattedMessage id="Vehicle" />, ["optional"])}
                  {this.makeSortHeader("startMeter", <FormattedMessage id="Duration" />, ["right"])}
                  {this.makeSortHeader("stopMeter", <FormattedMessage id="Energy" />, ["right"])}
                  {showCosts &&
                    this.makeSortHeader("costs", <FormattedMessage id="Costs" />, ["right"])}
                  {showCosts &&
                    this.makeSortHeader("fk_tariff", <FormattedMessage id="Tariff" />, [
                      "optional-lg",
                    ])}
                  {showStopReason &&
                    this.makeSortHeader("stopReason", <FormattedMessage id="Stop Reason" />, [
                      "optional-lg",
                    ])}
                </tr>
              </thead>
              <tbody>{dataItems}</tbody>
              {dataItems.length > 1 && (
                <tfoot>{this.renderSumRow(showUser, showCosts, showStopReason, showCar)}</tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    );
  }

  private renderChargerFilter(): React.ReactNode {
    return (
      <>
        <SearchFilter
          className="tableSearchBoxSidebar mb-1"
          filterText={this.state.filterText}
          placeholder={this.props.intl.formatMessage(messages.placeholderSearchField)}
          tooltip={this.props.intl.formatMessage(messages.toolTipSearchField)}
          onChange={(filterText) => {
            this.setState({ filterText });
          }}
          datalist="chargerDataList"
        />
        {this.renderChargerOptions("chargerDataList")}
      </>
    );
  }

  private renderChargerOptions(inId: string) {
    const chargerItems = this.props.ocppChargers.map((curCharger) => {
      return <option key={curCharger.fk_charger} value={curCharger.chargepoint} />;
    });

    return <datalist id={inId}>{chargerItems}</datalist>;
  }
  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof IOcppChargingSession | "carName",
    caption: JSX.Element,
    classes?: string[],
    title?: string,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort,
      this.props.onSort,
      classes,
      title,
    );
  }

  private getSortedSessions() {
    const sortCol = this.props.tableSort?.column as keyof IOcppChargingSession | undefined;
    // data state is immutable so we need to copy it before sorting
    return [...this.props.chargingSession].sort((a, b) => {
      let keyA: string | number | undefined;
      let keyB: string | number | undefined;
      keyA = a[sortCol!];
      keyB = b[sortCol!];
      // special handling for special columns
      if (sortCol === "rfid") {
        keyA = this.getUsername(keyA as string, undefined);
        keyB = this.getUsername(keyB as string, undefined);
      } else if (sortCol === "fk_charger") {
        const chargerA = this.props.chargers.find((cur) => cur.fk_charger === a.fk_charger);
        keyA = a.rfid?.includes("_")
          ? a.rfid.split("_").pop()
          : chargerA?.name ?? chargerA?.chargepoint;
        const chargerB = this.props.chargers.find((cur) => cur.fk_charger === b.fk_charger);
        keyB = b.rfid?.includes("_")
          ? b.rfid.split("_").pop()
          : chargerB?.name ?? chargerB?.chargepoint;
      } else if (sortCol === "startMeter") {
        // column is misused as "duration"
        keyA = (a.stopTimestamp ?? 0) - a.startTimestamp;
        keyB = (b.stopTimestamp ?? 0) - b.startTimestamp;
      } else if (sortCol === "stopMeter") {
        // column is misused as "energy"
        keyA = (a.stopMeter ?? a.startMeter) - a.startMeter;
        keyB = (b.stopMeter ?? b.startMeter) - b.startMeter;
      } else if (sortCol === "stopReason") {
        keyA = this.getStopReasonString(a.stopReason);
        keyB = this.getStopReasonString(b.stopReason);
      } else if (sortCol === "fk_tariff") {
        keyA = this.props.tariffs.find((curItem) => curItem.id === keyA)?.name ?? keyA;
        keyB = this.props.tariffs.find((curItem) => curItem.id === keyB)?.name ?? keyB;
      }
      const sort = isSet(keyA) && isSet(keyB) ? (keyA > keyB ? 1 : -1) : isSet(keyB) ? 1 : -1;
      return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
    });
  }

  private renderFilter() {
    // text selection hint includes all rfids and names that we know
    const allRfids = [...this.props.knownRfids]; // clone this as the state is immutable
    allRfids.push(...this.props.rfids.map((curItem) => curItem.rfid));
    allRfids.push(
      ...this.props.rfids.filter((curItem) => curItem.name).map((curItem) => curItem.name!),
    );
    const datalist = [...new Set(allRfids)].sort().map((curItem) => {
      return (
        <option key={curItem} value={curItem}>
          {curItem}
        </option>
      );
    });

    return (
      <div id="filter" className="headerbuttons">
        <div className="left chargerFilter">
          <div className="mt-2">
            <FormattedMessage id="User Filter" />:
          </div>
          <input
            title={this.props.intl.formatMessage(messages.toolTipSearchField)}
            name="chargingSessionFilter.user"
            list="chargingSessionFilter.user"
            type="text"
            className="ms-1"
            size={33}
            value={this.props.chargingSessionFilter.user ?? ""}
            onChange={this.props.onItemChanged}
          ></input>
          <datalist id="chargingSessionFilter.rfid">{datalist}</datalist>
        </div>
        <PeriodSelect
          identifier="chargingSessionFilter"
          updateAction={ActionType.ACT_GET_OCPP_SESSIONS_REQ}
          periodOptions={[
            TimePeriod.DAY,
            TimePeriod.WEEK,
            TimePeriod.MONTH,
            TimePeriod.YEAR,
            TimePeriod.ALL,
            TimePeriod.CUSTOM,
          ]}
          className="autoMarginLeft"
        />
      </div>
    );
  }

  private renderSearchFeedback() {
    return (
      <>
        {this.props.dataState === DataState.SAVE_REQ ? (
          <div className="ms-2 alert alert-warning">
            <FormattedMessage id="refreshing list..." />
          </div>
        ) : this.props.dataState === DataState.SAVE_FAIL ? (
          <div className="ms-2 alert alert-danger">
            <FormattedMessage id="Search failed." />
          </div>
        ) : (
          <></>
        )}
      </>
    );
  }

  private getUsername(rfid: string | undefined, rfidId: number | undefined) {
    // look, whether we have an rfid card with a name associated to this entry
    const user = this.props.rfids.find((curItem) => curItem.rfid === rfid || curItem.id === rfidId);
    if (user && user.category) {
      return `${user.name} (${user.category})`;
    }
    return user?.name ?? rfid;
  }

  private renderSession(
    data: IOcppChargingSession,
    showUser: boolean,
    showCosts: boolean,
    showStopReason: boolean,
    showCar: boolean,
  ) {
    const charger = this.props.chargers.find((curItem) => curItem.fk_charger === data.fk_charger);
    // a Keba slave charger delivers rfid_charger as the rfid code
    const rfid = data.rfid?.includes("_") ? data.rfid.split("_")[0] : data.rfid;
    const chargerName = data.rfid?.includes("_") ? (
      data.rfid.split("_").pop()
    ) : isSet(charger) ? (
      charger.name ?? charger.chargepoint
    ) : (
      <FormattedMessage id="RESTRICTED CHARGER" />
    );
    const hasSessionDetails = true;
    const carName = showCar
      ? this.props.rfids.find((curItem) => curItem.id === data.rfidId)?.carName
      : undefined;
    const tariff = this.props.tariffs.find((curItem) => curItem.id === data.fk_tariff);

    return (
      <tr
        key={data.id}
        className={hasSessionDetails ? "link" : undefined}
        tabIndex={hasSessionDetails ? 0 : undefined}
        onKeyDown={hasSessionDetails ? tabClickHandler : undefined}
        onClick={
          hasSessionDetails
            ? () => this.props.navigate(`../${MainPanel.CHARGINGSESSION}?transaction=${data.id}`)
            : undefined
        }
      >
        <td>
          <FormattedDate valueMs={data.startTimestamp * 1000} doZeroPadding={true} />{" "}
          <FormattedTime valueMs={data.startTimestamp * 1000} doZeroPadding={true} />
        </td>
        <td className="optional keep-print">
          {data.stopTimestamp ? (
            <>
              <FormattedDate valueMs={data.stopTimestamp * 1000} doZeroPadding={true} />{" "}
              <FormattedTime valueMs={data.stopTimestamp * 1000} doZeroPadding={true} />
            </>
          ) : (
            <FormattedMessage id="ongoing" />
          )}
        </td>
        <td className="optional charger-col">{chargerName}</td>
        {showUser && <td>{this.getUsername(rfid, data.rfidId)}</td>}
        {showCar && <td className="optional">{carName}</td>}
        <td className="right nowrap">
          {data.stopTimestamp && formatTimestamp(data.stopTimestamp - data.startTimestamp)}
        </td>
        <td className="right nowrap">
          {data.stopMeter && `${roundFix((data.stopMeter - data.startMeter) / 1000, 1)} kWh`}
        </td>
        {showCosts && (
          <td className="right nowrap">
            {isSet(data.costs) && `${roundFix(data.costs, 2)} ${tariff?.currency ?? "EUR"}`}
          </td>
        )}
        {showCosts && <td className="optional-lg">{tariff?.name}</td>}
        {showStopReason && (
          <td className="optional-lg">
            {data.stopTimestamp && this.getStopReasonString(data.stopReason)}
          </td>
        )}
      </tr>
    );
  }

  private getStopReasonString(stopReason: StopReason | undefined) {
    return this.props.intl.formatMessage({ id: `StopReason_${stopReason ?? ""}` });
  }

  private renderSumRow(
    showUser: boolean,
    showCosts: boolean,
    showStopReason: boolean,
    showCar: boolean,
  ) {
    // derive sums
    const energy = this.props.chargingSession.reduce((prevValue, curItem) => {
      if (curItem.stopMeter) {
        return prevValue + (curItem.stopMeter - curItem.startMeter) / 1000; // kWh
      }
      return prevValue; // do not add unfinished charging session
    }, 0);
    const duration = this.props.chargingSession.reduce((prevValue, curItem) => {
      if (curItem.stopTimestamp) {
        return prevValue + (curItem.stopTimestamp - curItem.startTimestamp); // s
      }
      return prevValue; // do not add unfinished charging session
    }, 0);
    const costs = showCosts
      ? this.props.chargingSession.reduce((prevValue, curItem) => {
          return prevValue + (curItem.costs ?? 0);
        }, 0)
      : 0;
    const tariff = showCosts
      ? this.props.tariffs.find(
          (curItem) => curItem.id === this.props.chargingSession[0]?.fk_tariff,
        )
      : undefined;

    return (
      <tr>
        <th>
          <FormattedMessage id="Sum" />
        </th>
        <th className="optional keep-print"></th>
        <th className="optional charger-col"></th>
        {showUser && <th></th>}
        {showCar && <th className="optional"></th>}
        <th className="right nowrap">{formatTimestamp(duration)}</th>
        <th className="right nowrap">{roundFix(energy, 1)} kWh</th>
        {showCosts && (
          <th className="right nowrap">
            {roundFix(costs, 2)} {tariff?.currency ?? "EUR"}
          </th>
        )}
        {showCosts && <th className="optional-lg"></th>}
        {showStopReason && <th className="optional-lg"></th>}
      </tr>
    );
  }
}

const ChargingSessions = injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(ChargingSessionListComponent)),
);

export default ChargingSessions;
